<template>
  <div>
    <section class="section auth-ui-section--has-margin-top auth-ui-section--is-narrow">
      <div class="container auth-ui-container--has-centered-content">
        <v-card :classes="'auth-ui-card--is-not-full-width'">
          <div slot="header-title">
            {{ $t('auth.signInTitle') }} {{ title }}
          </div>
          <form slot="body">
            <div class="field">
              <label class="label auth-ui-label">{{ $t('auth.signInEmailLabel') }}</label>
              <div class="control has-icons-left has-icons-right auth-ui-control-has-icons-left">
                <input
                  v-model="email"
                  :class="{ 'is-danger': errors.email }"
                  :placeholder="$t('auth.signInEmailPlaceholder')"
                  class="input auth-ui-input"
                  type="email"
                  autocomplete="email"
                  @blur="validateEmail()"
                  @focus="clearErrorFromField('email')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-envelope" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.email }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{ 'is-danger': errors.email, 'auth-ui-error--is-hidden': !errors.email }"
                class="help"
              >
                {{ $t('auth.signInEmailValidationError') }}
              </p>
            </div>

            <div class="field">
              <label class="label auth-ui-label">{{ $t('auth.signInPasswordLabel') }}</label>
              <div class="control has-icons-left has-icons-right auth-ui-control-has-icons-left">
                <input
                  v-model="password"
                  :class="{ 'is-danger': errors.password }"
                  :placeholder="$t('auth.signInPasswordPlaceholder')"
                  class="input auth-ui-input"
                  type="password"
                  autocomplete="password"
                  @blur="validatePassword()"
                  @focus="clearErrorFromField('password')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-lock" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.password }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{ 'is-danger': errors.password,
                          'auth-ui-error--is-hidden': !errors.password }"
                class="help"
              >
                {{ $t('auth.signInPasswordValidationError') }}
              </p>
            </div>

            <div class="field is-grouped is-grouped-centered">
              <div class="control auth-ui-button-and-spinner-container">
                <button
                  :disabled="signInButtonIsDisabled"
                  class="button is-link auth-ui-button"
                  @click.prevent="signIn()"
                >
                  {{ $t('auth.signInButton') }}
                </button>
              </div>
            </div>

            <article
              v-if="serverResponse.visible"
              class="auth-ui-card__message-container"
            >
              <div
                class="message is-danger"
              >
                <div class="message-body">
                  <button
                    class="delete auth-ui-delete--is-high"
                    aria-label="delete"
                    @click="showErrorMessage(false)"
                  />
                  <span>
                    {{ serverResponse.message }}
                  </span>
                </div>
              </div>
            </article>

            <div class="auth-ui-card-separator" />

            <div class="field is-grouped is-grouped-centered auth-ui-field--has-margin-top">
              <div class="control auth-ui-has-full-max-width">
                <div>
                  {{ $t('auth.signInRegisterLinkDescription') }}
                  <router-link
                    :to="registerLink"
                    class="auth-ui-form__link"
                  >
                    {{ $t('auth.signInRegisterLink') }}
                  </router-link>
                </div>
                <div>
                  {{ $t('auth.forgotPasswordLinkDescription') }}
                  <router-link
                    :to="passwordResetLink"
                    class="auth-ui-form__link"
                  >
                    {{ $t('auth.forgotPasswordLink') }}
                  </router-link>
                </div>
              </div>
            </div>
            <v-face-book-sign-in
              :button-pressed="buttonPressed"
              :toggle-button-pressed="toggleButtonPressed"
              :show-error-message="showErrorMessage"
            />
          </form>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
/* ============
       * Sign In Page
       * ============
       *
       * Page where the user can sign in.
       */

import Request from '../../tools/Request';
import VCard from '../../components/Card.vue';
import VFaceBookSignIn from '../../components/FacebookSignIn.vue';

export default {
  name: 'AuthSignIn',

  components: {
    VCard,
    VFaceBookSignIn,
  },

  data() {
    return {
      email: '',
      password: '',
      buttonPressed: false, // eliminates button spam
      errors: {
        email: false,
        password: false,
      },
      serverResponse: {
        message: '',
        visible: false,
      },
      signInIsDisabled: false,
      title: process.env.VUE_APP_PAGE_TITLE,
    };
  },

  computed: {
    signInButtonIsDisabled() {
      const fieldIsEmpty = !(this.email.length && this.password.length);
      return fieldIsEmpty || this.signInIsDisabled;
    },

    registerLink() {
      return {
        name: 'auth.register',
        query: this.$store.state.route.query,
      };
    },

    passwordResetLink() {
      return {
        name: 'auth.request.reset.password',
        query: this.$store.state.route.query,
      };
    },
  },

  mounted() {
    this.setInputFieldsToSubmitFormOnEnterPress();
  },

  methods: {
    clearErrorFromField(fieldName) {
      this.errors[fieldName] = false;
    },

    validateEmail() {
      this.errors.email = !this.email.length;
    },

    validatePassword() {
      this.errors.password = !this.password.length;
    },

    doFormValidations() {
      this.validateEmail();
      this.validatePassword();
    },

    closeMessage() {
      this.serverResponse.visible = false;
    },

    showErrorMessage(isVisible = true, message = '') {
      this.serverResponse.visible = isVisible;
      this.serverResponse.message = message;
    },

    signIn() {
      this.doFormValidations();

      if (!this.buttonPressed && !this.signInButtonIsDisabled) {
        this.toggleButtonPressed(true);
        this.toggleShowSpinner(true);
        this.showErrorMessage(false);

        Request.submit(
          'POST',
          `${process.env.VUE_APP_BACKEND_URL}/signin`,
          {
            email: this.email.toLowerCase(),
            password: this.password,
          },
        ).then((response) => {
          this.toggleShowSpinner(false);
          this.handleSignInSuccess(response);
        }).catch((error) => {
          this.toggleShowSpinner(false);
          this.toggleButtonPressed(false);
          if (error.error && (error.error === 'invalid_grant' || error.error === 'NotAuthorizedException')) {
            this.showErrorMessage(true, this.$t('auth.signInErrorMessage'));
          } else {
            this.showErrorMessage(true, this.$t('auth.signInServerErrorMessage'));
          }
        });
      }
    },

    setInputFieldsToSubmitFormOnEnterPress() {
      const inputs = document.getElementsByTagName('input');

      for (let i = 0; i < inputs.length; i += 1) {
        inputs[i].onkeydown = (e) => {
          if (e.keyCode === 13) {
            this.signIn();
          }
        };
      }
    },

    handleSignInSuccess(response) {
      if (response.signedIn) {
        this.signInIsDisabled = true;
        if (response.emailVerified) {
          const target = this.$store.state.route.query.target;
          if (target && target.length) {
            window.location.replace(decodeURI(target));
          } else {
            window.location.replace(`${process.env.VUE_APP_AFTER_SIGN_IN_URL}`);
          }
        } else {
          const navbar = document.getElementById('puheet-nav');
          const event = new CustomEvent('updatePuheetNavbar');
          navbar.dispatchEvent(event);
          window.location.replace(`${process.env.VUE_APP_AFTER_SIGN_IN_URL}`);
        }
      } else {
        // This should never happen, as the server returns "signedIn" in the response,
        // unless the response contains an error which is then handled in the catch block
        this.toggleButtonPressed(false);
        this.showErrorMessage(true, this.$t('auth.signInServerErrorMessage'));
      }
    },

    toggleButtonPressed(isPressed) {
      this.buttonPressed = isPressed;
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch('general/setShowSpinner', isVisible);
    },
  },
};
</script>
